/**
 * ChartUpdatesBucket — Finalization Tab
 *
 * Unified review panel for all proposed chart mutations from this encounter:
 *   1. Problem List Updates — new diagnoses, provider can modify ICD code before committing
 *   2. Medication Changes   — new meds / dose changes from AI suggestions
 *
 * Provider reviews each section, deselects anything incorrect, optionally changes
 * a diagnosis, then clicks "Apply to Chart". Safe to finalize without applying —
 * Sign & Finalize auto-applies any remaining pending items.
 */
import React from 'react';
import { api } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  ClipboardList, RefreshCw, CheckCircle2, CheckSquare, Square, Check,
  Loader2, AlertTriangle, Search, X, Edit3, Pill, ChevronDown, ChevronRight,
  Info, Stethoscope
} from 'lucide-react';
import { toast } from 'sonner';
import { BucketHeader, EmptyBucket } from './shared';

// ── Inline ICD-10 Search Modal ────────────────────────────────────────────────

function IcdSearchModal({ signalId, currentCode, currentName, onSelect, onClose }) {
  const [query, setQuery] = React.useState(currentName || '');
  const [results, setResults] = React.useState([]);
  const [loading, setLoading] = React.useState(false);

  const handleSearch = async () => {
    if (!query.trim()) return;
    setLoading(true);
    try {
      const res = await api.post('/icd10/search', { query });
      setResults(res.data.codes || []);
    } catch {
      toast.error('ICD search failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50/50 p-3 space-y-2 animate-in fade-in-0 slide-in-from-top-1 duration-100"
      data-testid={`icd-search-modal-${signalId}`}>
      <div className="flex items-center justify-between">
        <p className="text-[10px] font-semibold text-amber-800 uppercase tracking-wider flex items-center gap-1">
          <Edit3 className="w-3 h-3" /> Change Diagnosis Code
        </p>
        <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
      <div className="text-[10px] text-amber-700 flex items-center gap-1.5 flex-wrap">
        <span>Currently:</span>
        <Badge variant="outline" className="text-[8px] font-mono bg-amber-50 text-amber-700 border-amber-200">{currentCode}</Badge>
        <span>{currentName}</span>
      </div>
      <div className="flex gap-1.5">
        <input
          type="text"
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') handleSearch(); if (e.key === 'Escape') onClose(); }}
          placeholder='e.g. "type 2 diabetes", "E11.65"'
          className="flex-1 text-xs border rounded px-2 py-1.5 bg-background"
          autoFocus
          data-testid={`icd-search-input-${signalId}`}
        />
        <Button size="sm" className="h-8 text-[10px] px-3 gap-1" onClick={handleSearch} disabled={loading}>
          {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Search className="w-3 h-3" />}
          Search
        </Button>
      </div>
      {results.length > 0 && (
        <div className="max-h-48 overflow-y-auto space-y-0.5">
          {results.map(r => (
            <button
              key={r.code}
              onClick={() => { onSelect(signalId, r.code, r.description); onClose(); }}
              className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-left hover:bg-amber-100 transition-colors"
              data-testid={`icd-pick-${r.code}`}>
              <Badge variant="outline" className="text-[8px] font-mono bg-amber-50 text-amber-700 border-amber-200 shrink-0">{r.code}</Badge>
              <span className="text-xs flex-1 text-left">{r.description}</span>
              {r.billable === false && (
                <span className="text-[8px] text-muted-foreground shrink-0">non-billable</span>
              )}
            </button>
          ))}
        </div>
      )}
      {results.length === 0 && query.trim() && !loading && (
        <p className="text-xs text-muted-foreground text-center py-2">No results — try a different term</p>
      )}
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────

export function ChartUpdatesBucket({ enc, encounterId, fetchEncounter }) {
  const [preview, setPreview] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [applying, setApplying] = React.useState(false);
  const [applied, setApplied] = React.useState(false);
  // Selected signal IDs (all pre-selected by default)
  const [selectedIds, setSelectedIds] = React.useState(new Set());
  // Code overrides: { signal_id: { code, name } }
  const [codeOverrides, setCodeOverrides] = React.useState({});
  // Which signal has the ICD search open
  const [editingSignalId, setEditingSignalId] = React.useState(null);

  const fetchPreview = React.useCallback(async (force = false) => {
    setLoading(true);
    try {
      const endpoint = force
        ? `/encounters/${encounterId}/chart-update-preview/refresh`
        : `/encounters/${encounterId}/chart-update-preview`;
      const method = force ? 'post' : 'get';
      const res = await api[method](endpoint);
      const data = res.data;
      setPreview(data);
      // Pre-select all pending problem signals
      const pendingIds = (data.problem_signals || [])
        .filter(s => s.status === 'pending')
        .map(s => s.id);
      setSelectedIds(new Set(pendingIds));
    } catch {
      setPreview({ problem_signals: [], medication_updates: [], preventive_updates: [], pending_count: 0 });
    } finally {
      setLoading(false);
    }
  }, [encounterId]);

  React.useEffect(() => { fetchPreview(); }, [fetchPreview]);

  const handleCodeOverride = (signalId, newCode, newName) => {
    setCodeOverrides(prev => ({ ...prev, [signalId]: { code: newCode, name: newName } }));
    setEditingSignalId(null);
  };

  const toggleSignal = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    const pendingIds = (preview?.problem_signals || []).filter(s => s.status === 'pending').map(s => s.id);
    const allSelected = pendingIds.every(id => selectedIds.has(id));
    setSelectedIds(allSelected ? new Set() : new Set(pendingIds));
  };

  const handleApply = async () => {
    const pendingSignals = (preview?.problem_signals || []).filter(s => s.status === 'pending');
    const toApply = pendingSignals.filter(s => selectedIds.has(s.id));
    if (toApply.length === 0) {
      toast.info('No problems selected to add');
      return;
    }
    setApplying(true);
    try {
      // Build code overrides map: signal_id → new code string
      const overrideMap = {};
      Object.entries(codeOverrides).forEach(([sid, info]) => {
        overrideMap[sid] = info.code;
      });

      const res = await api.post(`/encounters/${encounterId}/chart-update-preview/apply`, {
        approved_signal_ids: toApply.map(s => s.id),
        code_overrides: overrideMap,
      });
      const { applied: n, errors = [] } = res.data;
      if (errors.length) errors.forEach(e => toast.warning(`Skipped: ${e}`));
      toast.success(
        n > 0
          ? `${n} problem${n !== 1 ? 's' : ''} added to problem list`
          : 'Chart updated — all problems already in list'
      );
      setApplied(true);
      if (fetchEncounter) fetchEncounter();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to apply updates');
    } finally {
      setApplying(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-10 justify-center text-muted-foreground">
        <Loader2 className="w-4 h-4 animate-spin" />
        <span className="text-xs">Scanning encounter for chart updates…</span>
      </div>
    );
  }

  const signals = preview?.problem_signals || [];
  const medUpdates = preview?.medication_updates || [];
  const pendingSignals = signals.filter(s => s.status === 'pending');
  const isEmpty = signals.length === 0 && medUpdates.length === 0;

  const allPendingSelected = pendingSignals.length > 0 && pendingSignals.every(s => selectedIds.has(s.id));
  const selectedCount = pendingSignals.filter(s => selectedIds.has(s.id)).length;

  return (
    <div className="space-y-5" data-testid="chart-updates-bucket">
      {/* Header */}
      <div className="flex items-center justify-between">
        <BucketHeader
          icon={ClipboardList}
          title="Chart Updates"
          count={pendingSignals.length + medUpdates.filter(m => !m.disposition || m.disposition === 'unreviewed').length}
          status={applied || isEmpty ? 'complete' : pendingSignals.length === 0 ? 'complete' : 'pending'}
        />
        <Button variant="ghost" size="sm" className="h-7 text-[10px] gap-1 text-muted-foreground"
          onClick={() => { setApplied(false); fetchPreview(true); }}
          data-testid="chart-updates-refresh-btn">
          <RefreshCw className="w-3 h-3" /> Refresh
        </Button>
      </div>

      {/* Info Banner */}
      {!applied && pendingSignals.length > 0 && (
        <div className="rounded-lg border border-sky-200 bg-sky-50/40 px-4 py-3 flex items-start gap-3">
          <Info className="w-4 h-4 text-sky-600 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-sky-800">Review before signing</p>
            <p className="text-[11px] text-sky-700 mt-0.5">
              These diagnoses were identified in this encounter. Review and click{' '}
              <strong>Apply to Chart</strong> — or they will be automatically applied when you sign and finalize.
              Click any diagnosis to change the ICD-10 code.
            </p>
          </div>
        </div>
      )}

      {/* Applied success state */}
      {applied && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 px-4 py-3 flex items-center gap-3">
          <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
          <div>
            <p className="text-xs font-semibold text-emerald-800">Chart updated</p>
            <p className="text-[11px] text-emerald-700">Problem list has been updated from this encounter.</p>
          </div>
          <Button variant="ghost" size="sm" className="h-7 text-[10px] ml-auto text-emerald-700"
            onClick={() => { setApplied(false); fetchPreview(true); }}>
            Review again
          </Button>
        </div>
      )}

      {/* ── Section 1: Problem List Updates ── */}
      {pendingSignals.length > 0 && !applied && (
        <div className="rounded-xl border border-amber-200 bg-amber-50/20 overflow-hidden"
          data-testid="problem-updates-section">
          {/* Section header with select all */}
          <div className="px-4 py-3 border-b border-amber-100 flex items-center gap-2 bg-amber-50/40">
            <button onClick={toggleAll} className="shrink-0" data-testid="select-all-signals">
              {allPendingSelected
                ? <CheckSquare className="w-4 h-4 text-amber-700" />
                : <Square className="w-4 h-4 text-amber-400" />}
            </button>
            <Stethoscope className="w-3.5 h-3.5 text-amber-700 shrink-0" />
            <p className="text-[11px] font-semibold text-amber-800 flex-1">
              {pendingSignals.length} diagnosis{pendingSignals.length !== 1 ? 'es' : ''} detected — add to Problem List
            </p>
            <span className="text-[9px] text-amber-600/70">{selectedCount} selected</span>
          </div>

          {/* Signal rows */}
          <div className="divide-y divide-amber-100/60">
            {pendingSignals.map(sig => {
              const isChecked = selectedIds.has(sig.id);
              const override = codeOverrides[sig.id];
              const displayCode = override?.code || sig.icd_code;
              const displayName = override?.name || sig.condition_name;
              const isEditing = editingSignalId === sig.id;

              return (
                <div key={sig.id} className="px-4 py-2.5" data-testid={`signal-row-${sig.icd_code_normalized}`}>
                  <div className="flex items-start gap-2">
                    {/* Checkbox */}
                    <button
                      onClick={() => toggleSignal(sig.id)}
                      className="shrink-0 mt-0.5"
                      data-testid={`signal-check-${sig.icd_code_normalized}`}>
                      {isChecked
                        ? <CheckSquare className="w-4 h-4 text-amber-700" />
                        : <Square className="w-4 h-4 text-muted-foreground/40" />}
                    </button>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge
                          variant="outline"
                          className={`text-[9px] font-mono shrink-0 ${override ? 'bg-emerald-50 text-emerald-700 border-emerald-300' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>
                          {displayCode}
                          {override && <span className="ml-1 text-[8px]">changed</span>}
                        </Badge>
                        <span className="text-xs font-medium text-foreground flex-1 min-w-0">{displayName}</span>
                        {sig.is_nonspecific && !override && (
                          <span className="text-[8px] text-amber-600 flex items-center gap-0.5 shrink-0">
                            <AlertTriangle className="w-2.5 h-2.5" /> Non-specific
                          </span>
                        )}
                        <span className="text-[8px] text-muted-foreground/60 shrink-0">
                          {sig.source === 'billing_dx' ? 'billing' : 'A&P'}
                        </span>
                      </div>

                      {/* Change code button */}
                      <button
                        onClick={() => setEditingSignalId(isEditing ? null : sig.id)}
                        className="mt-1 text-[9px] text-amber-600 hover:text-amber-800 flex items-center gap-0.5 transition-colors"
                        data-testid={`change-code-${sig.icd_code_normalized}`}>
                        <Edit3 className="w-2.5 h-2.5" />
                        {isEditing ? 'Cancel change' : 'Change diagnosis code'}
                        {isEditing ? <ChevronDown className="w-2.5 h-2.5" /> : <ChevronRight className="w-2.5 h-2.5" />}
                      </button>

                      {/* Inline ICD search */}
                      {isEditing && (
                        <IcdSearchModal
                          signalId={sig.id}
                          currentCode={displayCode}
                          currentName={displayName}
                          onSelect={handleCodeOverride}
                          onClose={() => setEditingSignalId(null)}
                        />
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Apply button */}
          <div className="px-4 py-3 border-t border-amber-100 bg-amber-50/40 flex items-center gap-3">
            <Button
              className="gap-1.5 bg-amber-700 hover:bg-amber-800 text-white flex-1 h-9 text-xs"
              disabled={selectedCount === 0 || applying}
              onClick={handleApply}
              data-testid="apply-chart-updates-btn">
              {applying
                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                : <Check className="w-3.5 h-3.5" />}
              Apply {selectedCount > 0 ? `${selectedCount} ` : ''}Problem{selectedCount !== 1 ? 's' : ''} to Chart
            </Button>
            <span className="text-[10px] text-amber-700/60 shrink-0">
              or Sign &amp; Finalize to auto-apply
            </span>
          </div>
        </div>
      )}

      {/* Resolved / already applied signals (historical) */}
      {signals.filter(s => s.status !== 'pending').length > 0 && (
        <div className="space-y-1">
          <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Already in Chart</p>
          {signals.filter(s => s.status !== 'pending').map(sig => (
            <div key={sig.id} className="flex items-center gap-2 py-1.5 opacity-60">
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
              <Badge variant="outline" className="text-[8px] font-mono shrink-0">{sig.icd_code}</Badge>
              <span className="text-xs text-muted-foreground">{sig.condition_name}</span>
            </div>
          ))}
        </div>
      )}

      {/* ── Section 2: Medication Changes ── */}
      {medUpdates.length > 0 && (
        <div className="space-y-2" data-testid="medication-updates-section">
          <div className="flex items-center gap-2">
            <Pill className="w-3.5 h-3.5 text-violet-600" />
            <p className="text-[11px] font-semibold text-violet-700">Medication Changes</p>
            <span className="text-[9px] text-muted-foreground ml-1">— managed in Prescriptions tab</span>
          </div>
          {medUpdates.slice(0, 4).map((m, i) => (
            <div key={m.id || i}
              className="rounded-lg border border-violet-100 bg-violet-50/20 px-3 py-2 flex items-center gap-3">
              <Badge variant="outline" className={`text-[8px] shrink-0 ${m.medication_action === 'new' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-sky-50 text-sky-700 border-sky-200'}`}>
                {m.medication_action === 'new' ? 'New' : 'Change'}
              </Badge>
              <span className="text-xs font-medium flex-1">{m.medication_name}</span>
              {m.medication_details && (
                <span className="text-[10px] text-muted-foreground shrink-0">{m.medication_details}</span>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Empty state */}
      {isEmpty && (
        <EmptyBucket
          icon={ClipboardList}
          message="No chart updates — generate a note first, then new diagnoses and changes will appear here"
        />
      )}

      {/* All done after finalized note */}
      {!isEmpty && signals.length > 0 && pendingSignals.length === 0 && !applied && (
        <div className="flex items-center gap-2 text-xs text-emerald-700 py-2">
          <CheckCircle2 className="w-4 h-4" />
          <span>Problem list is up to date — no new diagnoses detected</span>
        </div>
      )}
    </div>
  );
}
